import {
  useState,
  useEffect,
  useCallback,
  RefObject,
  KeyboardEvent,
} from 'react'

import { UseFocusableListOptions } from './useFocusableList.types'

export const useFocusableList = (
  listboxRef: RefObject<HTMLUListElement>,
  {
    isListOpened,
    optionsLength,
    onSelect,
    onClose,
    onOpen,
  }: UseFocusableListOptions
) => {
  const [focusedIndex, setFocusedIndex] = useState<null | number>(null)

  useEffect(() => {
    if (focusedIndex !== null) {
      const listboxNode = listboxRef.current!

      if (listboxNode && listboxNode.scrollHeight > listboxNode.clientHeight) {
        const element = listboxNode.querySelector(
          `[data-option-index="${focusedIndex}"]`
        )! as HTMLElement

        const scrollBottom = listboxNode.clientHeight + listboxNode.scrollTop
        const elementBottom = element.offsetTop + element.offsetHeight

        if (elementBottom > scrollBottom) {
          listboxNode.scrollTop = elementBottom - listboxNode.clientHeight
        } else if (element.offsetTop < listboxNode.scrollTop) {
          listboxNode.scrollTop = element.offsetTop
        }
      }
    }
  }, [listboxRef, focusedIndex])

  const handleInputKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (
        (isListOpened && e.key === 'ArrowDown') ||
        (isListOpened && !e.shiftKey && e.key === 'Tab')
      ) {
        e.preventDefault()

        if (focusedIndex !== null && focusedIndex >= optionsLength - 1) {
          setFocusedIndex(0)
        } else {
          setFocusedIndex((active) => (active !== null ? active + 1 : 0))
        }

        return
      }

      if (
        (isListOpened && e.key === 'ArrowUp') ||
        (isListOpened && e.shiftKey && e.key === 'Tab')
      ) {
        e.preventDefault()

        if (focusedIndex !== null && focusedIndex === 0) {
          setFocusedIndex(optionsLength - 1)
        } else {
          setFocusedIndex((active) => (active !== null ? active - 1 : 0))
        }

        return
      }

      if (e.key === 'Enter' && focusedIndex !== null) {
        onSelect(focusedIndex)

        setFocusedIndex(null)
        e.preventDefault()

        return
      }

      if (['Enter', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
        onOpen()

        return
      }

      if (['Escape'].includes(e.key)) {
        setFocusedIndex(null)
        onClose()
      }
    },
    [focusedIndex, isListOpened, onOpen, onClose, onSelect, optionsLength]
  )

  return [focusedIndex, handleInputKeyDown, setFocusedIndex] as const
}
